import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Award, Users, TrendingUp, Linkedin, MessageCircle, ArrowRight } from 'lucide-react';
import { COMPANY_INFO } from '../data/mock';
import { WA_URL, LINKEDIN_HIGOR } from '../lib/utils';
import Reveal from '../components/Reveal';
import GoogleReviews from '../components/GoogleReviews';

const CEO_PHOTO = 'https://customer-assets.emergentagent.com/job_talent-match-304/artifacts/pu3agut1_306D3747-F5F8-4192-A942-FDDA7352DAD8.png';

const SobrePage = () => {
  return (
    <div>
      {/* HERO */}
      <section className="hm-gradient text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm px-6 text-center relative">
          <Reveal>
            <p className="text-sm uppercase tracking-widest text-white/85 font-bold mb-3">Sobre Nós</p>
            <h1 className="text-4xl md:text-6xl font-extrabold">Quem somos nós</h1>
            <p className="mt-4 text-lg text-white/95 max-w-2xl mx-auto">Especialistas em conectar talentos a oportunidades, com agilidade, transparência e assertividade.</p>
          </Reveal>
        </div>
      </section>

      {/* CEO + INSTITUCIONAL */}
      <section className="section-padding bg-white">
        <div className="container-hm grid lg:grid-cols-2 gap-12 items-center">
          <Reveal variant="slideRight">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] max-w-md mx-auto bg-amber-100 card-shadow">
              <img src={CEO_PHOTO} alt="Higor Monteiro - CEO HM Talent" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/75 to-transparent">
                <p className="text-white font-bold text-base">{COMPANY_INFO.ceo}</p>
                <p className="text-white/90 text-sm">CEO HM Talent | Consultor Estratégico de R&S</p>
              </div>
            </div>
          </Reveal>

          <Reveal variant="slideLeft">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Nossa História</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Recrutamento que faz a diferença</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">A HM Talent nasceu com o propósito de revolucionar o recrutamento no Brasil, oferecendo um serviço ágil, transparente e com altos níveis de assertividade. Sob a liderança de <strong>{COMPANY_INFO.ceo}</strong>, conectamos +200 empresas aos profissionais certos, realizando mais de 3.000 contratações.</p>
            <p className="mt-3 text-slate-700 leading-relaxed">Nosso método combina tecnologia, expertise em hunting e análise comportamental (Solides DISC Profiler) para garantir contratações que duram — reduzindo turnover e aumentando a produtividade dos times.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a href={LINKEDIN_HIGOR} target="_blank" rel="noopener noreferrer" className="btn-hm">
                <Linkedin className="w-5 h-5" /> LinkedIn de Higor Monteiro
              </a>
              <a href={WA_URL('Olá, quero conversar com a HM Talent.')} target="_blank" rel="noopener noreferrer" className="btn-hm-outline">
                <MessageCircle className="w-5 h-5" /> Falar no WhatsApp
              </a>
            </div>
            <div className="mt-7 grid grid-cols-2 gap-4">
              <div className="hm-gradient-warm text-white p-5 rounded-2xl card-shadow">
                <div className="text-3xl font-extrabold" style={{ fontFamily: 'Montserrat' }}>+3.000</div>
                <div className="text-xs text-white/95 mt-1">Contratações realizadas</div>
              </div>
              <div className="bg-hm-yellow border border-amber-200 p-5 rounded-2xl">
                <div className="text-3xl font-extrabold text-hm-orange" style={{ fontFamily: 'Montserrat' }}>95%</div>
                <div className="text-xs text-slate-700 mt-1">Taxa de assertividade</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MISSÃO / VISÃO / VALORES */}
      <section className="section-padding bg-hm-yellow">
        <div className="container-hm">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Pilares</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Missão, Visão e Valores</h2>
            <p className="mt-3 text-slate-700">Os pilares que guiam nossas ações e nossa relação com empresas e candidatos.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: 'Missão', text: 'Conectar empresas e talentos com agilidade, transparência e assertividade.' },
              { icon: Eye, title: 'Visão', text: 'Ser referência nacional em recrutamento e seleção com inovação contínua.' },
              { icon: Heart, title: 'Valores', text: 'Ética, comprometimento, excelência, foco no resultado e respeito às pessoas.' },
            ].map((m, i) => (
              <Reveal key={i} delay={i * 100} variant="scale" className={`p-7 rounded-2xl hover-lift card-shadow ${i === 1 ? 'hm-gradient text-white' : 'bg-white border border-amber-100'}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${i === 1 ? 'bg-white/20' : 'hm-gradient'}`}>
                  <m.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${i === 1 ? 'text-white' : 'text-slate-900'}`}>{m.title}</h3>
                <p className={`text-sm leading-relaxed ${i === 1 ? 'text-white/95' : 'text-slate-700'}`}>{m.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RECONHECIMENTOS */}
      <section className="section-padding bg-white">
        <div className="container-hm grid md:grid-cols-3 gap-6 text-center">
          {[
            { icon: Award, title: 'Excelência premiada', text: 'Reconhecidos pela qualidade entregue e satisfação dos parceiros.' },
            { icon: Users, title: 'Time especialista', text: 'Profissionais experientes nas mais diversas áreas de atuação.' },
            { icon: TrendingUp, title: 'Crescimento contínuo', text: 'Investimos em tecnologia e métodos para entregar mais resultados.' },
          ].map((m, i) => (
            <Reveal key={i} delay={i * 100} className={`p-7 rounded-2xl hover-lift card-shadow ${i === 1 ? 'hm-gradient-warm text-white' : 'bg-hm-yellow border border-amber-100'}`}>
              <m.icon className={`w-12 h-12 mx-auto ${i === 1 ? 'text-white' : 'text-hm-orange'}`} />
              <h3 className={`text-xl font-bold mt-4 ${i === 1 ? 'text-white' : 'text-slate-900'}`}>{m.title}</h3>
              <p className={`text-sm mt-2 ${i === 1 ? 'text-white/95' : 'text-slate-700'}`}>{m.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AVALIAÇÕES GOOGLE */}
      <GoogleReviews />

      {/* CTA FINAL */}
      <section className="section-padding hm-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 80% 30%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm text-center relative">
          <h2 className="text-3xl md:text-4xl font-extrabold">Quer fazer parte da nossa rede?</h2>
          <p className="mt-3 text-white/95 max-w-2xl mx-auto">Cadastre-se no nosso banco de talentos ou conheça nossas soluções para empresas.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/portal/candidato/cadastro" className="btn-hm-white"><Users className="w-5 h-5" /> Sou Candidato</Link>
            <Link to="/para-empresas" className="btn-hm-ghost">Sou Empresa <ArrowRight className="w-5 h-5" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SobrePage;
